import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import dayjs from 'dayjs';
import { useMemo } from 'react';

import { type OpportunityType, useOpportunitiesList } from '@/api/opportunity';
import { RangeFilter } from '@/components/filters';
import {
  InstantSearchDataTable,
  type InstantSearchDataTableColumnDef,
  type SolQueryFilter,
  useInstantSearchState,
} from '@/components/InstantSearch';
import { getSolQueryParamsNewFromQueryState } from '@/components/InstantSearch/util/search-util';
import { usePageFilterContext } from '@/components/page';
import { Panel } from '@/components/panels';
import { Link } from '@/components/typography';
import { USDateViewFormat } from '@/constants';
import { OpportunityStatusTag } from '@/features/tags';
import { numberFormat } from '@/helper/numberFormatter';
import { ROUTES } from '@/router';

type Props = {
  companyId?: string;
};

const CompanyDetailOpportunitiesTable = ({ companyId }: Props) => {
  const {
    pageFilters: { startDate, endDate },
  } = usePageFilterContext();
  const { queryState } = useInstantSearchState();

  const solQueryParams = useMemo(() => {
    const solQueryParams = getSolQueryParamsNewFromQueryState(queryState);
    if (!solQueryParams || !companyId) {
      return undefined;
    }

    return {
      ...solQueryParams,
      filter: [
        ...solQueryParams.filter,
        {
          field: 'companyId',
          operator: 'in',
          operand: [companyId],
        } satisfies SolQueryFilter,
      ],
    };
  }, [queryState, companyId]);

  const { opportunityOverview, totalResults, isLoading, error } = useOpportunitiesList(
    solQueryParams,
    startDate,
    endDate,
  );

  const columns = useMemo<InstantSearchDataTableColumnDef<OpportunityType>[]>(
    () => [
      {
        header: t`Name`,
        id: 'name',
        accessorKey: 'name',
        enableSorting: true,
        minSize: 160,
        flex: 1,
        cell: ({ row }) => (
          <Link variant="caption1" to={ROUTES.opportunityById(row.original.id)}>
            {row.original.name}
          </Link>
        ),
      },
      {
        header: t`Open Date`,
        id: 'openDate',
        accessorKey: 'openDate',
        meta: { align: 'end' },
        enableSorting: true,
        size: 120,
        cell: ({ row }) => dayjs(row.original.openDate).format(USDateViewFormat),
      },
      {
        header: t`Close Date`,
        id: 'closeDate',
        accessorKey: 'closeDate',
        meta: { align: 'end' },
        enableSorting: true,
        size: 120,
        cell: ({ row }) => dayjs(row.original.closeDate).format(USDateViewFormat),
      },
      {
        header: t`Status`,
        id: 'status',
        accessorKey: 'status',
        meta: { align: 'end' },
        enableSorting: true,
        size: 120,
        cell: ({ row }) => <OpportunityStatusTag opportunity={row.original} />,
      },
      {
        header: t`Amount`,
        id: 'amount',
        accessorKey: 'amount',
        meta: { align: 'end' },
        enableSorting: true,
        size: 120,
        filterComponent: (props) => <RangeFilter {...props} />,
        cell: ({ row }) => numberFormat(row.original.amount, { isCurrency: true, precision: 0 }),
      },
    ],
    [],
  );

  return (
    <Panel title={t`Opportunities`}>
      <InstantSearchDataTable<OpportunityType>
        columns={columns}
        data={opportunityOverview}
        totalResults={totalResults}
        loading={isLoading}
        error={error}
        getRowId={(record) => record.id}
        emptyMessage={<Trans>No opportunities found.</Trans>}
        initialColumnPinning={{
          left: ['name'],
        }}
      />
    </Panel>
  );
};

export default CompanyDetailOpportunitiesTable;
